import { __assign, __rest } from "tslib";
import React, { useRef } from 'react';
import clsx from 'clsx';
import InternalIcon from '../icon/internal';
import styles from './styles.css.js';
import useFocusVisible from '../internal/hooks/focus-visible';
import { getBaseProps } from '../internal/base-component';
import { fireCancelableEvent, isPlainLeftClick } from '../internal/events';
import useForwardFocus from '../internal/hooks/forward-focus';
import { KeyCode } from '../internal/keycode';
import mergeRefs from 'react-merge-refs';
var ICON_SIZES = {
    'body-s': 'small',
    'body-m': 'normal',
    'heading-xs': 'normal',
    'heading-s': 'normal',
    'heading-m': 'normal',
    'heading-l': 'medium',
    'heading-xl': 'big',
    'display-l': 'large'
};
var InternalLink = React.forwardRef(function (_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? 'secondary' : _b, _c = _a.fontSize, fontSize = _c === void 0 ? 'body-m' : _c, _d = _a.color, color = _d === void 0 ? 'normal' : _d, _e = _a.external, external = _e === void 0 ? false : _e, target = _a.target, href = _a.href, ariaLabel = _a.ariaLabel, externalIconAriaLabel = _a.externalIconAriaLabel, onFollow = _a.onFollow, children = _a.children, _f = _a.__internalRootRef, __internalRootRef = _f === void 0 ? null : _f, props = __rest(_a, ["variant", "fontSize", "color", "external", "target", "href", "ariaLabel", "externalIconAriaLabel", "onFollow", "children", "__internalRootRef"]);
    var isButton = !href;
    var focusVisible = useFocusVisible();
    var baseProps = getBaseProps(props);
    var anchorTarget = target !== null && target !== void 0 ? target : (external ? '_blank' : undefined);
    var anchorRel = anchorTarget === '_blank' ? 'noopener noreferrer' : undefined;
    var fireFollowEvent = function (event) {
        fireCancelableEvent(onFollow, { href: href, external: external, target: anchorTarget }, event);
    };
    var handleLinkClick = function (event) {
        if (isPlainLeftClick(event)) {
            fireFollowEvent(event);
        }
    };
    var handleButtonClick = function (event) {
        fireFollowEvent(event);
    };
    var handleButtonKeyDown = function (event) {
        // Prevent the page from scrolling down when spacebar is pressed.
        if (event.keyCode === KeyCode.space) {
            event.preventDefault();
        }
    };
    var handleButtonKeyUp = function (event) {
        if (event.keyCode === KeyCode.space || event.keyCode === KeyCode.enter) {
            fireFollowEvent(event);
        }
    };
    var linkRef = useRef(null);
    useForwardFocus(ref, linkRef);
    var sharedProps = __assign(__assign(__assign({}, focusVisible), baseProps), { 
        // https://github.com/microsoft/TypeScript/issues/36659
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref: mergeRefs([linkRef, __internalRootRef]), className: clsx(styles.link, baseProps.className, styles["weight-" + (variant === 'secondary' ? 'normal' : 'bold')], styles["font-size-" + (variant === 'info' ? 'body-s' : fontSize)], styles["color-" + (variant === 'info' ? 'normal' : color)]), 'aria-label': ariaLabel });
    var content = (React.createElement(React.Fragment, null,
        children,
        external && (React.createElement(React.Fragment, null,
            ' ',
            React.createElement("span", { className: styles.icon, "aria-label": externalIconAriaLabel, role: externalIconAriaLabel ? 'img' : undefined },
                React.createElement(InternalIcon, { name: "external", size: variant === 'info' ? 'small' : ICON_SIZES[fontSize] }))))));
    if (isButton) {
        return (React.createElement("a", __assign({}, sharedProps, { role: "button", tabIndex: 0, onKeyDown: handleButtonKeyDown, onKeyUp: handleButtonKeyUp, onClick: handleButtonClick }), content));
    }
    return (
    // we dynamically set proper rel in the code above
    // eslint-disable-next-line react/jsx-no-target-blank
    React.createElement("a", __assign({}, sharedProps, { target: anchorTarget, rel: anchorRel, href: href, onClick: handleLinkClick }), content));
});
export default InternalLink;
//# sourceMappingURL=internal.js.map